import React from 'react';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  .static-page {
    font-family: 'Inter', system-ui, sans-serif;
    background: #0a0f1e;
    min-height: 100vh;
    color: #e2e8f0;
  }

  .static-hero {
    background: linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #0a0f1e 100%);
    padding: 80px 24px 70px;
    text-align: center;
    position: relative;
    overflow: hidden;
    border-bottom: 1px solid rgba(99,102,241,0.15);
  }

  .static-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 70% 60% at 50% -10%, rgba(99,102,241,0.18) 0%, transparent 70%);
    pointer-events: none;
  }

  .static-label {
    display: inline-block;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: #a5b4fc;
    background: rgba(99,102,241,0.1);
    border: 1px solid rgba(99,102,241,0.25);
    border-radius: 100px;
    padding: 4px 14px;
    margin-bottom: 20px;
  }

  .static-hero h1 {
    font-size: clamp(2rem, 5vw, 3.2rem);
    font-weight: 900;
    color: #f1f5f9;
    margin: 0 0 16px;
    letter-spacing: -0.03em;
  }

  .static-hero h1 span {
    background: linear-gradient(135deg, #818cf8, #6366f1);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .static-hero-sub {
    font-size: 1.05rem;
    color: #64748b;
    max-width: 540px;
    margin: 0 auto;
    line-height: 1.7;
  }

  .static-meta { font-size: 12px; color: #475569; margin-top: 12px; }

  .static-body {
    max-width: 800px;
    margin: 0 auto;
    padding: 64px 24px 100px;
  }

  .static-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(99,102,241,0.12);
    border-radius: 20px;
    padding: 40px;
    margin-bottom: 28px;
    transition: border-color 0.3s;
  }

  .static-card:hover { border-color: rgba(99,102,241,0.25); }

  .static-card h2 {
    font-size: 1.15rem;
    font-weight: 800;
    color: #f1f5f9;
    margin: 0 0 16px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .card-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px; height: 34px;
    background: rgba(99,102,241,0.15);
    border-radius: 9px;
    font-size: 16px;
    flex-shrink: 0;
  }

  .static-card p, .static-card li {
    font-size: 0.93rem;
    line-height: 1.8;
    color: #94a3b8;
    margin: 0 0 10px;
  }

  .static-card ul {
    padding-left: 20px;
    margin: 8px 0;
  }

  .static-card li::marker { color: #6366f1; }
  .static-card li { margin-bottom: 6px; }
  .static-card strong { color: #c7d2fe; }

  @media (max-width: 560px) {
    .static-card { padding: 28px 20px; }
  }
`;

const Terms = () => (
  <div className="static-page">
    <style>{STYLES}</style>

    <div className="static-hero">
      <span className="static-label">Legal</span>
      <h1>Terms of <span>Service</span></h1>
      <p className="static-hero-sub">
        By using RDRTO, you agree to these terms. Please read them carefully — they're
        written to be clear and fair.
      </p>
      <p className="static-meta">Last updated: March 01, 2026</p>
    </div>

    <div className="static-body">

      <div className="static-card">
        <h2><span className="card-icon">✅</span>Acceptance of Terms</h2>
        <p>
          By accessing or using RDRTO (rdrto.com), you confirm that you are at least 13 years old
          and agree to be bound by these Terms of Service and our Privacy Policy. If you do not
          agree, please do not use the service.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">📋</span>Use License</h2>
        <p>
          RDRTO grants you a personal, non-exclusive, non-transferable, revocable licence to
          use the platform for personal and commercial link management purposes, subject to
          the following restrictions:
        </p>
        <ul>
          <li>You may not resell, sublicense, or commercially redistribute the service itself</li>
          <li>You may not attempt to decompile, reverse-engineer, or copy the platform</li>
          <li>You may not use the service in a way that violates any applicable law or regulation</li>
          <li>You may not interfere with or disrupt the service's infrastructure or other users</li>
        </ul>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">👤</span>User Accounts</h2>
        <p>
          When you create an account you must provide accurate, current, and complete information.
          You are responsible for:
        </p>
        <ul>
          <li>Maintaining the confidentiality of your login credentials</li>
          <li>All activity that occurs under your account</li>
          <li>Notifying us immediately at admin@rdrto.com if you suspect unauthorized access</li>
        </ul>
        <p>
          We reserve the right to suspend or terminate accounts that provide false information
          or violate these terms.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">🚫</span>Acceptable Use</h2>
        <p>You agree <strong>not</strong> to use RDRTO to shorten or share links that lead to:</p>
        <ul>
          <li>Phishing, malware, or other malicious content</li>
          <li>Illegal goods, services, or activities</li>
          <li>Content that infringes intellectual property rights</li>
          <li>Spam, unsolicited messaging, or bulk commercial messages</li>
          <li>Hate speech, harassment, or content that promotes violence</li>
          <li>Adult content without appropriate age-gating</li>
        </ul>
        <p>
          We actively monitor for abuse and will suspend links and accounts without notice
          when violations are detected.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">⚙️</span>Service Availability</h2>
        <p>
          We strive for 99.9% uptime but do not guarantee uninterrupted, error-free service.
          We may perform scheduled or emergency maintenance that temporarily affects availability.
          We reserve the right to modify, suspend, or discontinue any feature of the service
          at any time with reasonable notice where possible.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">💳</span>Payment Terms</h2>
        <p>
          Premium plans are billed in advance on a monthly or annual cycle as selected at
          sign-up. All payments are processed securely by Stripe. Prices are subject to change
          with 30 days' notice.
        </p>
        <ul>
          <li>Payments are non-refundable except where required by applicable law</li>
          <li>Failed payments may result in plan downgrade after a grace period</li>
          <li>You may cancel your subscription at any time; access continues until the end of the billing period</li>
        </ul>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">⚖️</span>Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, RDRTO and its operators shall not be liable for
          any indirect, incidental, special, consequential, or punitive damages — including loss
          of data, revenue, or business — arising out of or in connection with your use of the service,
          even if we have been advised of the possibility of such damages.
        </p>
        <p>
          Our total aggregate liability to you for any claim shall not exceed the amount you paid
          us in the 12 months preceding the claim.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">🔄</span>Changes to These Terms</h2>
        <p>
          We may update these terms from time to time. When we do, we'll update the date at the top
          of this page and — for material changes — notify you by email. Continued use of RDRTO
          after changes take effect constitutes your acceptance of the updated terms.
        </p>
      </div>

      <div className="static-card">
        <h2><span className="card-icon">📬</span>Contact</h2>
        <p>
          Questions about these Terms? Email us at{' '}
          <a href="mailto:admin@rdrto.com" style={{ color: '#a5b4fc', textDecoration: 'none' }}>admin@rdrto.com</a>.
          We respond within 24 hours on business days.
        </p>
      </div>

    </div>
  </div>
);

export default Terms;
